import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner, Table, Badge } from "react-bootstrap";

import { request } from "hooks/useOkoApi/useOkoApi";

/**
 * Tab 4 — Link Schools (Feature #16, Step 1: the human-gated org->school map).
 *
 * A client-keyed EDNITION credential's SIS feed can span multiple schools. Here
 * a superuser discovers that credential's Ednition `orgs` and links each
 * `type=school` org to an EXISTING OKO School. The backend writes the
 * IntegrationEntity(entity_type='school') provenance the daily/manual sync then
 * consumes to resolve each student's/class's school PER-ENTITY.
 *
 * Backend contract (all superuser-guarded, secret-free):
 *   GET    integrations/ednition/orgs?credential_id  -> {orgs:[OrgLinkRow]}
 *   POST   integrations/ednition/orgs/link           -> upsert (409 on N:1)
 *   DELETE integrations/ednition/orgs/link/{org}      -> {removed: bool}
 *
 * District orgs come back `linkable=false` (read-only context); only school
 * orgs get a School dropdown. org->school is strictly 1:1: linking a School
 * already used by another org is rejected 409.
 */
export const LinkSchoolsTab = () => {
  const [credentials, setCredentials] = useState([]);
  const [clientNameById, setClientNameById] = useState({});
  const [schools, setSchools] = useState([]);
  const [credentialId, setCredentialId] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [creds, clientData] = await Promise.all([
          request("integrations/credentials", { method: "GET" }),
          request("admin/clients", { method: "GET" }),
        ]);
        setCredentials(creds?.credentials || []);
        const names = {};
        (clientData?.clients || []).forEach((c) => (names[c.id] = c.name));
        setClientNameById(names);
      } catch (e) {
        setError(e?.detail || "Failed to load credentials");
      }
    })();
  }, []);

  // Only client-keyed EDNITION rostering credentials expose multiple schools.
  const clientKeyedCredentials = useMemo(
    () =>
      credentials.filter(
        (c) => c.provider === "EDNITION" && c.client_id && !c.school_id
      ),
    [credentials]
  );

  const loadOrgs = async (id) => {
    if (!id) {
      setOrgs([]);
      setSchools([]);
      return;
    }
    setError(null);
    setNotice(null);
    setLoadingOrgs(true);
    // Scope the OKO School dropdown to the credential's client — a client-keyed
    // credential should only offer schools that belong to that client.
    const credential = clientKeyedCredentials.find((c) => c.id === id);
    try {
      const query = new URLSearchParams({ credential_id: id });
      const schoolQuery = new URLSearchParams();
      if (credential?.client_id) schoolQuery.set("client_id", credential.client_id);
      const [orgData, schoolData] = await Promise.all([
        request(`integrations/ednition/orgs?${query.toString()}`, {
          method: "GET",
        }),
        request(`admin/schools?${schoolQuery.toString()}`, { method: "GET" }),
      ]);
      setOrgs(orgData?.orgs || []);
      setSchools(schoolData?.schools || []);
    } catch (e) {
      setOrgs([]);
      setSchools([]);
      setError(
        e?.detail ||
          "Failed to fetch orgs from RosterStream (the org fetch may have failed)."
      );
    } finally {
      setLoadingOrgs(false);
    }
  };

  const onCredentialChange = (id) => {
    setCredentialId(id);
    loadOrgs(id);
  };

  const setBusy = (sourcedId, busy) =>
    setRowBusy((prev) => ({ ...prev, [sourcedId]: busy }));

  const linkOrg = async (org, okoSchoolId) => {
    setError(null);
    setNotice(null);
    if (!okoSchoolId) return;
    setBusy(org.sourced_id, true);
    try {
      await request("integrations/ednition/orgs/link", {
        method: "POST",
        payload: {
          credential_id: credentialId,
          org_sourced_id: org.sourced_id,
          oko_school_id: okoSchoolId,
        },
      });
      const school = schools.find((s) => s.id === okoSchoolId);
      setOrgs((prev) =>
        prev.map((o) =>
          o.sourced_id === org.sourced_id
            ? {
                ...o,
                linked: true,
                oko_school_id: okoSchoolId,
                oko_school_name: school ? school.name : null,
              }
            : o
        )
      );
      setNotice(`Linked "${org.name || org.sourced_id}" to ${school?.name || okoSchoolId}.`);
    } catch (e) {
      // 409 = the chosen School is already linked to another org (strict 1:1).
      setError(
        e?.detail === "SchoolAlreadyLinked"
          ? "That OKO School is already linked to another Ednition org (links are 1:1). Pick a different School or unlink the other org first."
          : e?.detail || "Failed to link the org."
      );
    } finally {
      setBusy(org.sourced_id, false);
    }
  };

  const unlinkOrg = async (org) => {
    setError(null);
    setNotice(null);
    setBusy(org.sourced_id, true);
    try {
      await request(`integrations/ednition/orgs/link/${encodeURIComponent(org.sourced_id)}`, {
        method: "DELETE",
      });
      setOrgs((prev) =>
        prev.map((o) =>
          o.sourced_id === org.sourced_id
            ? { ...o, linked: false, oko_school_id: null, oko_school_name: null }
            : o
        )
      );
      setNotice(`Unlinked "${org.name || org.sourced_id}".`);
    } catch (e) {
      setError(e?.detail || "Failed to unlink the org.");
    } finally {
      setBusy(org.sourced_id, false);
    }
  };

  const schoolOrgs = orgs.filter((o) => o.linkable);
  const districtOrgs = orgs.filter((o) => !o.linkable);
  const linkedCount = schoolOrgs.filter((o) => o.linked).length;

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}

      <Form.Group controlId="linkCredential">
        <Form.Label>Client-keyed Ednition credential</Form.Label>
        <Form.Control
          as="select"
          data-testid="link-credential-select"
          value={credentialId}
          onChange={(e) => onCredentialChange(e.target.value)}
        >
          <option value="">Select a credential…</option>
          {clientKeyedCredentials.map((c) => (
            <option key={c.id} value={c.id}>
              {c.provider} · {c.environment} ·{" "}
              {clientNameById[c.client_id] || c.client_id}
            </option>
          ))}
        </Form.Control>
        {clientKeyedCredentials.length === 0 && (
          <Form.Text className="text-muted">
            No client-keyed EDNITION credentials found. Org→school linking applies
            only to client-keyed credentials (a school-keyed credential already
            has its one school).
          </Form.Text>
        )}
      </Form.Group>

      {loadingOrgs && (
        <div style={{ marginTop: "24px" }}>
          <Spinner animation="border" size="sm" /> Discovering orgs…
        </div>
      )}

      {!loadingOrgs && credentialId && schoolOrgs.length === 0 && (
        <Alert variant="warning" style={{ marginTop: "24px" }}>
          This credential returned no school orgs.
        </Alert>
      )}

      {!loadingOrgs && schoolOrgs.length > 0 && (
        <>
          <div style={{ marginTop: "24px", marginBottom: "8px" }}>
            <strong>School orgs</strong>{" "}
            <Badge variant="secondary" data-testid="linked-count">
              {linkedCount}/{schoolOrgs.length} linked
            </Badge>
          </div>
          <Table striped bordered size="sm" data-testid="school-orgs-table">
            <thead>
              <tr>
                <th>Ednition school</th>
                <th>Identifier</th>
                <th>OKO School</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schoolOrgs.map((org) => {
                const busy = !!rowBusy[org.sourced_id];
                return (
                  <tr key={org.sourced_id} data-testid={`org-row-${org.sourced_id}`}>
                    <td>
                      {org.name || org.sourced_id}
                      {org.linked && (
                        <Badge variant="success" style={{ marginLeft: "8px" }}>
                          linked
                        </Badge>
                      )}
                    </td>
                    <td>{org.identifier || "—"}</td>
                    <td>
                      <Form.Control
                        as="select"
                        size="sm"
                        data-testid={`school-select-${org.sourced_id}`}
                        value={org.oko_school_id || ""}
                        disabled={busy}
                        onChange={(e) => linkOrg(org, e.target.value)}
                      >
                        <option value="">Not linked</option>
                        {schools.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </Form.Control>
                    </td>
                    <td>
                      {org.linked && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={busy}
                          data-testid={`unlink-${org.sourced_id}`}
                          onClick={() => unlinkOrg(org)}
                        >
                          {busy ? "…" : "Unlink"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}

      {!loadingOrgs && districtOrgs.length > 0 && (
        <div style={{ marginTop: "16px" }} className="text-muted">
          <small>
            District (context, not linkable):{" "}
            {districtOrgs.map((o) => o.name || o.sourced_id).join(", ")}
          </small>
        </div>
      )}
    </div>
  );
};
